import { h } from '@omni-torn/ui';
import {
  FLIGHTS_KEEP_DAYS,
  KEEP_ALL_TERMS,
  FLIGHTS_MAX_IDS,
  FLIGHTS_PATH,
  FLIGHTS_PLAYER_PATH,
  FLIGHTS_TERMS,
  guessText,
  legWindows,
  likelyWindows,
  readFlightPlayer,
  readFlights,
  routeText,
  TRAVEL_TYPE_LABEL,
  type FlightLeg,
  type FlightPlayerAnswer,
  type FlightsAnswer,
  type MethodWindow,
  type PlaneGuess,
} from '@omni-torn/shared';
import { server } from '../session';
import { ago, box, duration, problem, table, tct } from '../ui';

/**
 * The flight tracker (website roadmap S23 — FF Scouter premium's flight tracker), drawn on the player page (lookup) and in
 * the war room (Faction → War): the flight a player is on (where to, when they took off, when they land for each way of
 * flying that fits, "airstrip/business likely"), a stay abroad, and their last flights. Data: GET /api/flights* — what
 * Omni-Torn kept (terms 11; 90 days): faction watches and what Omni-Torn users saw. Nothing is read from Torn here.
 * The timing: packages/shared flights.ts. Torn's time (TCT) is UTC.
 */

export const FLIGHTS_NOTE = `From what Omni-Torn sees — factions watched with members’ Discord-bot keys and players Omni-Torn users saw (from terms ${FLIGHTS_TERMS}) — so many flights are missed, and windows are only as tight as the sightings. Times are Torn time (TCT).`;

/** "Mon 14:05" within the week, else "10-24 14:05". */
const when = (ms: number): string => (Math.abs(Date.now() - ms) < 6 * 86_400_000 ? tct(ms) : `${new Date(ms).toISOString().slice(5, 10)} ${tct(ms).slice(-5)}`);
/** A window of two moments: one when they're within a minute. */
const span = (a: number, b: number): string => (b - a < 60_000 ? when(b) : `${when(a)}–${tct(b).slice(-5)}`);
const mins = (ms: number): string => `${Math.round(ms / 60_000)}m`;

/** "Lands 15:40–15:55 TCT (in 1h 20m)" / "should have landed by 15:55 TCT". */
function landText(w: MethodWindow, now: number): string {
  if (w.landLatest < now) return `should have landed by ${when(w.landLatest)} TCT`;
  const mid = (w.landEarliest + w.landLatest) / 2;
  return `${span(w.landEarliest, w.landLatest)} TCT (in ${w.landEarliest < now ? 'up to ' : '~'}${duration(((w.landEarliest < now ? w.landLatest : mid) - now) / 1000)})`;
}

/** "Airstrip or business class likely — 3 of 4 timed flights were too quick for standard" / "Usually standard (2 timed flights)". */
export function guessLine(g: PlaneGuess | null): HTMLElement | null {
  if (!g) return null;
  if (g.fastLikely) return h('p', { 'data-flights': 'guess', class: 'flag-warn' }, `⚡ Flies faster than standard — ${guessText(g)} likely: ${g.fast} of ${g.timed} timed flight${g.timed === 1 ? ' was' : 's were'} too quick for standard.`);
  return h('p', { 'data-flights': 'guess', class: 'muted' }, `Usually ${guessText(g)} (${g.timed} timed flight${g.timed === 1 ? '' : 's'}).`);
}

/** The flight they're on: route, take-off, and each way of flying that fits (the likely ones marked). */
function currentPart(l: FlightLeg, guess: PlaneGuess | null, now: number): HTMLElement[] {
  const { likely, fits } = likelyWindows(l, guess);
  const tookOff = l.afterAt !== null ? `took off ${span(l.afterAt, l.firstAt)} TCT` : `took off by ${when(l.firstAt)} TCT`;
  return [
    h('p', { 'data-flights': 'current' }, h('strong', {}, `✈ ${routeText(l)}`), ` · ${tookOff} · seen flying ${ago(l.lastAt)}`),
    table(
      fits,
      [
        { label: 'Way of flying', get: (w) => (likely.includes(w) && likely.length < fits.length ? h('strong', {}, `${TRAVEL_TYPE_LABEL[w.type]} ★`) : TRAVEL_TYPE_LABEL[w.type]) },
        { label: 'Flight', get: (w) => mins(w.flightMs), cls: 'r' },
        { label: 'Took off', get: (w) => `${span(w.departEarliest, w.departLatest)} TCT` },
        { label: 'Lands', get: (w) => landText(w, now) },
      ],
      { empty: 'No way of flying fits what was seen.' },
    ),
  ];
}

const fitsText = (l: FlightLeg): string => {
  const f = legWindows(l).filter((w) => w.possible);
  return f.length === 4 ? 'any' : f.length ? f.map((w) => TRAVEL_TYPE_LABEL[w.type]).join(', ') : '—';
};

// ---------------------------------------------------------------- the player page

const asked = new Map<string, { at: number; p: Promise<unknown> }>();
function ask(path: string): Promise<unknown> {
  const had = asked.get(path);
  if (had && Date.now() - had.at < 60_000) return had.p;
  const p = server<unknown>('GET', path);
  asked.set(path, { at: Date.now(), p });
  p.catch(() => asked.delete(path));
  return p;
}

/** "Flights" on the player page: the flight they're on, a stay abroad, how they usually fly, their last flights. */
export async function playerFlightsBox(id: number): Promise<HTMLElement> {
  let a: FlightPlayerAnswer | null;
  try {
    a = readFlightPlayer(await ask(`${FLIGHTS_PLAYER_PATH}/${id}`));
  } catch (err) {
    return box('Flights', problem(err), { id: 'player-flights' });
  }
  if (!a || (!a.current && !a.abroad && !a.recent.length)) return box('Flights', [h('p', { class: 'muted' }, 'Omni-Torn hasn’t seen them fly yet.'), h('p', { class: 'muted small' }, FLIGHTS_NOTE)], { id: 'player-flights' });
  const now = Date.now();
  return box(
    'Flights',
    [
      ...(a.current ? currentPart(a.current, a.guess, now) : []),
      !a.current && a.abroad ? h('p', { 'data-flights': 'abroad' }, h('strong', {}, `In ${a.abroad.destination}`), ` since ${when(a.abroad.since)} TCT or earlier · seen there ${ago(a.abroad.seenAt)}`) : null,
      guessLine(a.guess),
      a.recent.length
        ? table(
            a.recent,
            [
              { label: 'Seen flying', get: (l) => `${when(l.firstAt)} TCT`, sort: (l) => l.firstAt },
              { label: 'Route', get: (l) => routeText(l), sort: (l) => routeText(l) },
              { label: 'Took off', get: (l) => (l.afterAt !== null ? `${span(l.afterAt, l.firstAt)}` : `by ${when(l.firstAt)}`) },
              { label: 'Landed by', get: (l) => (l.landedAt !== null ? when(l.landedAt) : h('span', { class: 'muted' }, 'not seen')) },
              { label: 'Ways that fit', get: (l) => fitsText(l) },
            ],
            { initial: 0, desc: true, empty: 'No earlier flights seen.' },
          )
        : null,
      h('p', { class: 'muted small' }, FLIGHTS_NOTE, a.keepDays === 0 ? ' Kept with no end date.' : ` Kept ${a.keepDays || FLIGHTS_KEEP_DAYS} days — with no end date when a player who saw it agreed to terms version ${KEEP_ALL_TERMS}.`),
    ],
    { id: 'player-flights', extra: h('span', { class: 'muted small', 'data-flights': 'count' }, `${a.recent.length + (a.current ? 1 : 0)} flight${a.recent.length + (a.current ? 1 : 0) === 1 ? '' : 's'}`) },
  );
}

// ---------------------------------------------------------------- the war room

type WarFlight = FlightsAnswer['players'][number];
const war: { key: string; at: number; asking: boolean; problem: string; byId: Map<number, WarFlight> } = { key: '', at: 0, asking: false, problem: '', byId: new Map() };

/** The war room asks for its enemy roster's flights (once a minute, or when the roster changes); `redraw` when they come. */
export function wantFlights(ids: readonly number[], redraw: () => void): void {
  const ask0 = [...new Set(ids)].slice(0, FLIGHTS_MAX_IDS).sort((a, b) => a - b);
  const key = ask0.join(',');
  if (!ask0.length || war.asking || (key === war.key && Date.now() - war.at < 60_000)) return;
  war.asking = true;
  server<unknown>('GET', `${FLIGHTS_PATH}?ids=${key}`)
    .then((b) => {
      war.byId = new Map(readFlights(b).players.map((p) => [p.id, p]));
      war.problem = '';
    })
    .catch((err: Error) => (war.problem = err.message))
    .finally(() => {
      war.key = key;
      war.at = Date.now();
      war.asking = false;
      redraw();
    });
}

/** The status cell's line when the bot has no landing window: "➤ Japan · lands 15:40–15:55 TCT" from the flight tracker. */
export function flightStatus(id: number, state: string): HTMLElement | null {
  const f = war.byId.get(id);
  if (!f?.current || !/travel/i.test(state)) return null;
  const { likely } = likelyWindows(f.current, f.guess);
  if (!likely.length) return null;
  const lo = Math.min(...likely.map((w) => w.landEarliest));
  const hi = Math.max(...likely.map((w) => w.landLatest));
  const where = f.current.leg === 'out' ? f.current.destination : 'Torn';
  return h('span', { class: 'st-blue', title: `Omni-Torn’s flight tracker: ${likely.map((w) => TRAVEL_TYPE_LABEL[w.type]).join(' or ')}` }, `➤ ${where} · lands ${hi < Date.now() ? `by ${tct(hi)}` : `${tct(lo)}–${tct(hi)}`} TCT${f.guess?.fastLikely ? ' ⚡' : ''}`);
}

/** "Flights" box under the enemy roster: who's flying or abroad, take-off, landing by the likely way of flying, the flag. */
export function warFlightsBox(theirs: ReadonlyArray<{ id: number; name: string }>): HTMLElement {
  const now = Date.now();
  const rows = theirs.flatMap((m) => {
    const f = war.byId.get(m.id);
    return f && (f.current || f.abroad) ? [{ m, f }] : [];
  });
  return box(
    `Flights (${rows.length})`,
    [
      war.problem ? h('p', { class: 'muted small' }, war.problem) : null,
      table(
        rows,
        [
          { label: 'Member', get: (r) => h('a', { href: `/lookup/${r.m.id}`, 'data-link': true }, r.m.name), sort: (r) => r.m.name.toLowerCase() },
          { label: 'Where', get: (r) => (r.f.current ? `✈ ${routeText(r.f.current)}` : `In ${r.f.abroad!.destination}`), sort: (r) => (r.f.current ? 0 : 1) },
          { label: 'Took off', get: (r) => (r.f.current ? (r.f.current.afterAt !== null ? `${span(r.f.current.afterAt, r.f.current.firstAt)} TCT` : `by ${when(r.f.current.firstAt)} TCT`) : `there since ${when(r.f.abroad!.since)} TCT`) },
          {
            label: 'Lands (likely)',
            get: (r) => {
              if (!r.f.current) return h('span', { class: 'muted' }, '—');
              const { likely } = likelyWindows(r.f.current, r.f.guess);
              return likely.length ? h('div', {}, ...likely.map((w) => h('div', {}, `${TRAVEL_TYPE_LABEL[w.type]}: ${landText(w, now)}`))) : h('span', { class: 'muted' }, '—');
            },
            sort: (r) => (r.f.current ? Math.min(...likelyWindows(r.f.current, r.f.guess).likely.map((w) => w.landLatest), Infinity) : Infinity),
          },
          { label: 'How they fly', get: (r) => (r.f.guess ? h('span', { class: r.f.guess.fastLikely ? 'flag-warn' : 'muted' }, r.f.guess.fastLikely ? `⚡ ${guessText(r.f.guess)} likely` : `usually ${guessText(r.f.guess)}`) : h('span', { class: 'muted' }, 'not known')) },
        ],
        { initial: 3, empty: 'Omni-Torn hasn’t seen any of them flying or abroad lately.', remember: 'war-flights' },
      ),
      h('p', { class: 'muted small' }, 'Omni-Torn’s flight tracker. ', FLIGHTS_NOTE),
    ],
    { id: 'war-flights' },
  );
}
